import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { weeklyGross, dailyRate, takeHome } from "./SalaryTable";
import ChangeLogNotes from "@/components/shared/ChangeLogNotes";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

function Money({ label, k, d, setField, emp, disabled }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-1 mt-1">
        <span className="text-slate-400">$</span>
        <Input type="number" step="0.01" min="0" value={d[k] ?? 0} onChange={(e) => setField(emp, k, e.target.value)} disabled={disabled} className="h-9" data-testid={`salary-modal-${k}`} />
      </div>
    </div>
  );
}

// Compact Edit Salary modal; edits the same draft as the grid. Money/Type changes go through the effective-date prompt on save.
export function SalaryEditDialog({ row, draft, setField, editUnlocked, onCancel, onSave, onRequestUnlock, saving }) {
  if (!row) return null;
  const emp = row.employee;
  const d = draft[emp] || {};
  const dis = !editUnlocked;
  return (
    <Dialog open={!!row} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="salary-edit-modal">
        <DialogHeader>
          <DialogTitle>Edit Salary — {emp}</DialogTitle>
          <DialogDescription>Weekly amounts. Changes to pay or Type ask when they take effect (from today or a past date) so history is kept.</DialogDescription>
        </DialogHeader>
        {dis && (
          <div className="text-xs rounded-md bg-slate-100 text-slate-600 px-3 py-2 flex items-center justify-between">
            Fields are locked.
            <Button size="sm" variant="outline" className="h-7" onClick={onRequestUnlock} data-testid="salary-modal-unlock">Unlock to edit</Button>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Type</Label>
            <Input value={d.emp_type ?? ""} onChange={(e) => setField(emp, "emp_type", e.target.value)} disabled={dis} maxLength={40} placeholder="e.g. 1099 or W2" className="h-9 mt-1" data-testid="salary-modal-emp_type" />
          </div>
          <div>
            <Label className="text-xs">Eff. Daily Rate <span className="text-slate-400 font-normal">(Weekly Total Gross ÷ 5, calculated)</span></Label>
            <div className="h-9 mt-1 flex items-center tabular-nums" data-testid="salary-modal-daily-rate"><span className="eff-daily">{money(dailyRate(d))}</span></div>
          </div>
          <Money label="Weekly Check" k="check_amount" d={d} setField={setField} emp={emp} disabled={dis} />
          <Money label="Weekly Cash" k="cash_amount" d={d} setField={setField} emp={emp} disabled={dis} />
          <Money label="Tax WH (weekly)" k="tax_wh_amount" d={d} setField={setField} emp={emp} disabled={dis} />
          <Money label="Addit. Pmt/Week" k="add_pay_amount" d={d} setField={setField} emp={emp} disabled={dis} />
          <div className="col-span-2">
            <Label className="text-xs">Additional Payment Note <span className="text-slate-400 font-normal">(what the additional payment is for)</span></Label>
            <Input value={d.additional_note ?? ""} onChange={(e) => setField(emp, "additional_note", e.target.value)} disabled={dis} maxLength={300} placeholder="e.g. Car insurance" className="h-9 mt-1" data-testid="salary-modal-additional_note" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Section</Label>
            <div className="flex gap-1.5 mt-1">
              {[["", "Employee salaries"], ["painters", "Painters"]].map(([v, l]) => (
                <button key={v || "main"} type="button" disabled={dis} onClick={() => setField(emp, "group", v)} data-testid={`salary-modal-group-${v || "main"}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors disabled:opacity-50 ${(d.group || "") === v ? "bg-[#327de3] border-[#327de3] text-white" : "bg-white border-slate-200 text-slate-600"}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm flex items-center justify-between" data-testid="salary-modal-gross-preview">
          <span className="text-slate-600 text-xs">Weekly Total Gross = Check + Cash + Tax WH + Addit. Pmt/Week</span>
          <span className="salary-gross font-extrabold tabular-nums text-base" data-testid="salary-modal-gross">{money(weeklyGross(d))}</span>
        </div>
        <div className="text-xs text-slate-600 flex justify-between px-3" data-testid="salary-modal-take-home">
          <span>Weekly Take Home Pay (excludes Tax WH)</span><span className="salary-gross font-bold tabular-nums">{money(takeHome(d))}</span>
        </div>
        <div className="border-t border-slate-200 pt-3" data-testid="salary-modal-notes-timeline">
          <div className="text-xs font-semibold text-slate-700 mb-1">Notes & change history</div>
          <ChangeLogNotes basePath={`/admin/salaries/${encodeURIComponent(emp)}`} idFieldLabel="employee" />
        </div>
        {row.updated_at && <div className="text-[11px] text-slate-400">Last updated {new Date(row.updated_at).toLocaleString()}{row.updated_by ? ` by ${row.updated_by}` : ""}</div>}
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="salary-modal-cancel">Cancel</Button>
          <Button onClick={onSave} disabled={dis || saving} className="bg-[#327de3] hover:bg-[#1f64c2] text-white" data-testid="salary-modal-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
